import { useEffect, useState } from 'react';
import Navigation from '@/components/navigation';
import Footer from '@/components/footer';
import SEO from '@/components/SEO';
import { Link } from 'wouter';
import { TrendingUp, MapPin, DollarSign, Home as HomeIcon } from 'lucide-react';

export default function InsightsTop10Deals() {
  const [content, setContent] = useState('');

  useEffect(() => {
    // Load the parasite SEO article content
    fetch('/api/insights/top-10-deals')
      .then(res => res.text())
      .then(html => setContent(html))
      .catch(error => console.error('Error loading content:', error));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Top 10 Waterfront Real Estate Deals in South Florida (2025 Hidden Gems)"
        description="Insider's guide to the best waterfront property deals in Miami, Fort Lauderdale, and Boca Raton. Expert analysis of undervalued oceanfront condos, bayfront homes, and investment opportunities."
        keywords="best waterfront deals South Florida, undervalued oceanfront properties, Miami real estate deals, Fort Lauderdale investment properties, Boca Raton waterfront bargains"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Top 10 Waterfront Real Estate Deals in South Florida: 2025 Hidden Gems",
          "description": "Insider's guide to the best waterfront property deals in Miami, Fort Lauderdale, and Boca Raton",
          "author": {
            "@type": "Organization",
            "name": "Selling South FL Waterfront"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Selling South FL Waterfront",
            "logo": {
              "@type": "ImageObject",
              "url": "https://sellingsouthflwaterfront.com/logo.png"
            }
          },
          "datePublished": "2025-11-19",
          "dateModified": "2025-11-19"
        }}
      />
      <Navigation />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <TrendingUp className="w-8 h-8" />
              <span className="text-lg font-semibold uppercase tracking-wider">Insider Insights</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Top 10 Waterfront Real Estate Deals in South Florida
            </h1>
            <p className="text-xl md:text-2xl opacity-90">
              Hidden gems and exceptional value opportunities in Miami, Fort Lauderdale, and Boca Raton
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="bg-gray-50 border-y border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className="w-6 h-6 text-blue-600" />
                <span className="text-3xl font-bold text-gray-900">$395K-$1.85M</span>
              </div>
              <p className="text-gray-600">Price Range</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <MapPin className="w-6 h-6 text-blue-600" />
                <span className="text-3xl font-bold text-gray-900">10</span>
              </div>
              <p className="text-gray-600">Prime Locations</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <HomeIcon className="w-6 h-6 text-blue-600" />
                <span className="text-3xl font-bold text-gray-900">8-12%</span>
              </div>
              <p className="text-gray-600">Potential Annual Returns</p>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="container mx-auto px-4 py-16 max-w-4xl">
        <div 
          className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4"
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* CTA Section */}
        <div className="mt-16 p-10 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl text-white text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Find Your Waterfront Deal?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get exclusive access to off-market listings and pre-market opportunities
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/buy">
              <a className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors">
                Browse Properties
              </a>
            </Link>
            <Link href="/about">
              <a className="inline-block bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-800 transition-colors border-2 border-white">
                Contact Expert Team
              </a>
            </Link>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Continue Reading
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link href="/blog/miami-beach-real-estate-market-2025">
              <a className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h4 className="font-bold text-lg text-gray-900 mb-2">Miami Beach Real Estate Market 2025</h4>
                <p className="text-gray-600">Complete market analysis and investment outlook</p>
              </a>
            </Link>
            <Link href="/blog/waterfront-investment-guide-south-florida">
              <a className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h4 className="font-bold text-lg text-gray-900 mb-2">Waterfront Investment Guide</h4>
                <p className="text-gray-600">ROI analysis and tax strategies for investors</p>
              </a>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

